import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Divider,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Add, Remove, Search } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { validatePhoneNumber, cleanPhoneNumber } from '../utils/validation';

const API_URL = process.env.REACT_APP_API_URL;

const DirectReportForm = ({ onSaved, onCancel }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Patient information
  const [patientInfo, setPatientInfo] = useState({
    name: '',
    phone: '',
    email: '',
    dateOfBirth: null,
    gender: '',
    address: '',
    height: '',
    weight: ''
  });
  const [phoneError, setPhoneError] = useState('');
  
  // Appointment information
  const [appointmentInfo, setAppointmentInfo] = useState({
    appointmentTime: new Date(),
    notes: '',
    referringDoctor: '',
    collectionAddress: ''
  });
  
  // Test selection
  const [testCategories, setTestCategories] = useState([]);
  const [selectedTests, setSelectedTests] = useState([]);
  const [testResults, setTestResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTests, setFilteredTests] = useState([]);

  const steps = ['Patient Information', 'Appointment Details', 'Test Selection', 'Test Results'];

  useEffect(() => {
    fetchTestCategories();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      searchTests();
    } else {
      setFilteredTests([]);
    }
  }, [searchTerm]);

  const fetchTestCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tests/categories`);
      setTestCategories(response.data);
    } catch (err) {
      setError('Failed to load test categories');
      console.error('Error fetching test categories:', err);
    }
  };

  const searchTests = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tests/all`);
      const filtered = response.data.filter(test => 
        test.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        test.testCategory.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredTests(filtered);
    } catch (err) {
      console.error('Error searching tests:', err);
    }
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleAddTest = (test) => {
    if (!selectedTests.find(t => t.id === test.id)) {
      setSelectedTests([...selectedTests, test]);
      setTestResults([...testResults, {
        testId: test.id,
        result: '',
        unit: test.unit,
        referenceRange: test.referenceRange,
        isAbnormal: false,
        notes: ''
      }]);
    }
  };

  const handleRemoveTest = (testId) => {
    setSelectedTests(selectedTests.filter(t => t.id !== testId));
    setTestResults(testResults.filter(r => r.testId !== testId));
  };

  const handleTestResultChange = (testId, field, value) => {
    setTestResults(testResults.map(result => 
      result.testId === testId ? { ...result, [field]: value } : result
    ));
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Only allow digits and common phone separators
    const cleanedValue = value.replace(/[^\d\s\-\(\)]/g, '');
    setPatientInfo({ ...patientInfo, phone: cleanedValue });
    
    // Validate phone number
    const validation = validatePhoneNumber(cleanedValue);
    if (!validation.isValid && cleanedValue.length > 0) {
      setPhoneError(validation.error);
    } else {
      setPhoneError('');
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 0: // Patient Information
        if (!patientInfo.name.trim()) return 'Patient name is required.';
        if (!patientInfo.gender) return 'Gender is required.';
        if (!patientInfo.dateOfBirth) return 'Date of birth is required.';
        if (!patientInfo.phone.trim()) return 'Phone number is required.';
        
        // Validate phone number
        const phoneValidation = validatePhoneNumber(patientInfo.phone);
        if (!phoneValidation.isValid) {
          return phoneValidation.error;
        }
        
        return null;
      case 1: // Appointment Details
        if (!appointmentInfo.appointmentTime) return 'Appointment time is required.';
        if (!appointmentInfo.referringDoctor.trim()) return 'Referring doctor is required.';
        return null;
      case 2: // Test Selection
        if (selectedTests.length === 0) return 'Please select at least one test.';
        return null;
      case 3: // Test Results
        // Validate test results
        for (const test of selectedTests) {
          if (!testResults[test.id]?.result?.trim()) {
            return `Please enter result for ${test.name}.`;
          }
        }
        return null;
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    const validationError = validateStep(activeStep);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Clean phone number before submission
      const phoneValidation = validatePhoneNumber(patientInfo.phone);
      const requestData = {
        patientDetails: {
          name: patientInfo.name,
          phone: phoneValidation.cleanPhone,
          email: patientInfo.email,
          dateOfBirth: patientInfo.dateOfBirth.toISOString().split('T')[0],
          gender: patientInfo.gender,
          address: patientInfo.address,
          height: patientInfo.height ? parseFloat(patientInfo.height) : null,
          weight: patientInfo.weight ? parseFloat(patientInfo.weight) : null
        },
        appointmentTime: appointmentInfo.appointmentTime.toISOString(),
        testIds: selectedTests.map(test => test.id),
        testResults: testResults,
        notes: appointmentInfo.notes,
        referringDoctor: appointmentInfo.referringDoctor,
        collectionAddress: appointmentInfo.collectionAddress
      };

      await axios.post(`${API_URL}/api/laboratorist/reports/create-direct`, requestData);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create report');
      console.error('Error creating report:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderPatientStep = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Patient Name *"
          value={patientInfo.name}
          onChange={(e) => setPatientInfo({ ...patientInfo, name: e.target.value })}
          margin="normal"
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Phone Number *"
          value={patientInfo.phone}
          onChange={handlePhoneChange}
          error={!!phoneError}
          helperText={phoneError || 'Enter 10-digit phone number'}
          margin="normal"
          required
          inputProps={{
            maxLength: 15,
            placeholder: '1234567890'
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Email"
          type="email"
          value={patientInfo.email}
          onChange={(e) => setPatientInfo({ ...patientInfo, email: e.target.value })}
          margin="normal"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Date of Birth *"
            value={patientInfo.dateOfBirth}
            onChange={(date) => setPatientInfo({ ...patientInfo, dateOfBirth: date })}
            renderInput={(params) => <TextField {...params} fullWidth margin="normal" required />}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth margin="normal" required>
          <InputLabel>Gender</InputLabel>
          <Select
            value={patientInfo.gender}
            label="Gender"
            onChange={(e) => setPatientInfo({ ...patientInfo, gender: e.target.value })}
          >
            <MenuItem value="MALE">Male</MenuItem>
            <MenuItem value="FEMALE">Female</MenuItem>
            <MenuItem value="OTHER">Other</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Height (cm)"
          type="number"
          value={patientInfo.height}
          onChange={(e) => setPatientInfo({ ...patientInfo, height: e.target.value })}
          margin="normal"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Weight (kg)"
          type="number"
          value={patientInfo.weight}
          onChange={(e) => setPatientInfo({ ...patientInfo, weight: e.target.value })}
          margin="normal"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Address"
          multiline
          rows={3}
          value={patientInfo.address}
          onChange={(e) => setPatientInfo({ ...patientInfo, address: e.target.value })}
          margin="normal"
        />
      </Grid>
    </Grid>
  );

  const renderAppointmentStep = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Appointment Date & Time *"
            value={appointmentInfo.appointmentTime}
            onChange={(date) => setAppointmentInfo({ ...appointmentInfo, appointmentTime: date })}
            renderInput={(params) => <TextField {...params} fullWidth margin="normal" required />}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Referring Doctor"
          value={appointmentInfo.referringDoctor}
          onChange={(e) => setAppointmentInfo({ ...appointmentInfo, referringDoctor: e.target.value })}
          margin="normal"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Collection Address"
          value={appointmentInfo.collectionAddress}
          onChange={(e) => setAppointmentInfo({ ...appointmentInfo, collectionAddress: e.target.value })}
          margin="normal"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Notes"
          multiline
          rows={3}
          value={appointmentInfo.notes}
          onChange={(e) => setAppointmentInfo({ ...appointmentInfo, notes: e.target.value })}
          margin="normal"
        />
      </Grid>
    </Grid>
  );

  const renderTestSelectionStep = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Search and Select Tests</Typography>
      
      <TextField
        fullWidth
        label="Search tests by name or category"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          endAdornment: <Search />
        }}
      />

      {filteredTests.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Available Tests</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {filteredTests.map((test) => (
                <Chip
                  key={test.id}
                  label={`${test.name} (${test.testCategory.name})`}
                  onClick={() => handleAddTest(test)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {selectedTests.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Selected Tests</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedTests.map((test) => (
                <Chip
                  key={test.id}
                  label={`${test.name} (${test.testCategory.name})`}
                  onDelete={() => handleRemoveTest(test.id)}
                  color="primary"
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );

  const renderTestResultsStep = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Enter Test Results</Typography>
      
      {testResults.map((result, index) => {
        const test = selectedTests.find(t => t.id === result.testId);
        return (
          <Card key={result.testId} sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {test?.name} ({test?.testCategory.name})
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Result *"
                    value={result.result}
                    onChange={(e) => handleTestResultChange(result.testId, 'result', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Unit"
                    value={result.unit}
                    onChange={(e) => handleTestResultChange(result.testId, 'unit', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Reference Range"
                    value={result.referenceRange}
                    onChange={(e) => handleTestResultChange(result.testId, 'referenceRange', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={result.isAbnormal ? 'abnormal' : 'normal'}
                      label="Status"
                      onChange={(e) => handleTestResultChange(result.testId, 'isAbnormal', e.target.value === 'abnormal')}
                    >
                      <MenuItem value="normal">Normal</MenuItem>
                      <MenuItem value="abnormal">Abnormal</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={2}
                    value={result.notes}
                    onChange={(e) => handleTestResultChange(result.testId, 'notes', e.target.value)}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return renderPatientStep();
      case 1:
        return renderAppointmentStep();
      case 2:
        return renderTestSelectionStep();
      case 3:
        return renderTestResultsStep();
      default:
        return null;
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ mt: 2, mb: 2 }}>
        {renderStepContent(activeStep)}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2 }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
        >
          Back
        </Button>
        
        <Box>
          <Button onClick={onCancel} sx={{ mr: 1 }}>
            Cancel
          </Button>
          
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading || !validateStep(activeStep)}
            >
              {loading ? <CircularProgress size={20} /> : 'Create Report'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!validateStep(activeStep)}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DirectReportForm; 